import type { SortingState } from '@tanstack/react-table';
import cx from 'clsx';
import { dateToLocalYMDHM } from 'common/date.ts';
import { MPUT, POST } from 'common/helpers.ts';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { mutate } from 'swr';
import useSWRInfinite from 'swr/infinite';
import type { Notification } from 'types/Notification.ts';
import { Button } from 'ui/component/Button.tsx';
import { Divider } from 'ui/component/Divider.tsx';
import { ICircle, IRight } from 'ui/component/Icons.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Tab } from 'ui/component/Tab.tsx';
import { ControlledToggle } from 'ui/control/ControlledToggle.tsx';
import axios from '#admin/axios.ts';
import { tableSortersToRepositorySort } from '#admin/component/table/index.ts';

export const getSWRKeyForPage =
  (path: string, pageSize: number, filters: Record<string, any>, sorters: SortingState | null, ...rest: any[]) =>
  (pageIndex: number, previousPageData: Notification[] | null) => {
    // if we got less than pageSize last time, there's nothing more to load
    if (previousPageData && previousPageData.length < pageSize) return null;
    // calculate the offset for the next page
    const offset = pageIndex * pageSize;
    // convert the sort array from swr to what api expects
    const sort = tableSortersToRepositorySort(sorters);
    // add the limit and offset to the filters (rest can contain any other param that should be part of the key and used by GET/POST fetchers)
    const key = [path, { ...filters, sort, limit: pageSize, offset }, ...rest];
    return key;
  };

const PAGE_SIZE = 20;
const NO_DATA = [] as Notification[];

export const NotificationList = () => {
  const { t } = useTranslation();
  const [tab, setTab] = React.useState('recent');
  const [currentNotification, setCurrentNotification] = React.useState<Notification | null>(null);
  const [unreadOnly, setUnreadOnly] = React.useState(false);
  const onChangeTab = (nextTab: string | number) => setTab(String(nextTab));

  const filters = useMemo(() => {
    const archivedFilters = tab === 'recent' ? { archived_at: null } : { archived_at$not: null };
    const unreadFilters = unreadOnly ? { read_at: null } : {};
    return { ...unreadFilters, ...archivedFilters };
  }, [tab, unreadOnly]);

  const {
    data: pages,
    isLoading,
    isValidating,
    size,
    setSize,
    mutate: refresh,
  } = useSWRInfinite(getSWRKeyForPage('/notification/list', PAGE_SIZE, filters, [{ id: 'created_at', desc: true }]), POST, {
    revalidateFirstPage: false,
  });

  const notifications = React.useMemo(() => pages?.flat() ?? NO_DATA, [pages]);

  React.useEffect(() => {
    if (!notifications?.length) return;
    if (size === 1) return; // don't scroll on first page load, the user just opened the page
    const element = document.getElementById('notification-list') as HTMLDivElement;
    if (!element) return;
    element.scrollTop = element.scrollHeight;
  }, [size, notifications]);

  const readNotification = React.useCallback(
    (notification) => async () => {
      // we avoid refetching the list, but we pretend is read: it will be marked as read when the component is rendered
      setCurrentNotification(Object.assign(notification, { read_at: new Date().toISOString() }));
      // flag as read once opened
      await MPUT(`/notification/${notification.id}/read`, {} as any);
      // refresh unread count
      void mutate((key: string[]) => Array.isArray(key) && key[0] === '/notification/count');
    },
    [],
  );

  React.useEffect(() => {
    if (!notifications?.length) return;
    readNotification(notifications[0])();
  }, [notifications, readNotification]);

  const onArchive = (notificationId) => async () => {
    await MPUT(`/notification/${notificationId}/archive`, {} as any);
    await mutate((key: string[]) => Array.isArray(key) && ['/notification/list', '/notification/count'].includes(key[0]));
    void refresh();
  };

  const sendFakeNotification = async () => {
    await axios.get('/notification/test');
    void refresh();
    void mutate((key: string[]) => Array.isArray(key) && key[0] === '/notification/count');
  };

  const onChangeUnreadOnly = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUnreadOnly(event.target.checked);
  };

  const hasMore = pages ? pages[pages.length - 1]?.length === PAGE_SIZE : false;

  return (
    <main className="view-container">
      <section className="mb-4 flex flex-row place-content-between items-center">
        <h4>{t('navigation.notifications')}</h4>
        {import.meta.env.VITE_APP_ENV !== 'production' && (
          <Button className="red fixed right-20 bottom-20 z-90 w-min" onClick={sendFakeNotification}>
            TEST
          </Button>
        )}
      </section>
      <section className="paper">
        <Tab tabs={{ recent: t('components.alerts.tabs.recent'), archived: t('components.alerts.tabs.archived') }} defaultTab={'recent'} onChange={onChangeTab} />
        {tab === 'recent' && <ControlledToggle label="Unread only" labelPosition="right" value={unreadOnly} onChange={onChangeUnreadOnly} className="mt-2 p-4 pb-0" />}
        <div className="relative flex min-h-[calc(400px)] flex-row gap-3 pt-3">
          {isLoading && <Spinner centered={true} />}
          {!isLoading && !notifications.length && <p className="flex w-full place-content-center place-items-center text-xl">{t('pushNotifications.noNotifications')}</p>}
          {!isLoading && !!notifications?.length && currentNotification && (
            <React.Fragment>
              <div id="notification-list" className="relative max-h-[calc(100vh-230px)] flex-1/4 overflow-auto">
                {notifications?.map((notification) => (
                  <NotificationListItem
                    key={notification.id}
                    className={cx(notification === currentNotification && 'bg-blue-sky')}
                    onClick={readNotification(notification)}
                    {...notification}
                  />
                ))}

                {hasMore && (
                  <div className="my-2 flex place-content-center place-items-center">
                    {isValidating && <Spinner size={24} />}
                    {!isValidating && (
                      <p onClick={() => setSize(size + 1)} className="anchor cursor-pointer">
                        {t('components.alerts.buttons.loadMore')}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <Divider direction="vertical" />
              <div className="col-span-2 flex-3/4 p-4 pt-2">
                <NotificationBox {...currentNotification} onArchive={onArchive} />
              </div>
            </React.Fragment>
          )}
        </div>
      </section>
    </main>
  );
};

export const NotificationListItem = ({ id, className, created_at, read_at, type, payload, onClick }: Notification & { className?: string; onClick?: any }) => {
  const { t } = useTranslation();

  return (
    <div className={cx('flex cursor-pointer flex-row gap-2 border-grey-border border-b p-4', className)} data-id={id} onClick={onClick}>
      <div className="flex flex-col gap-2">
        <span className="flex flex-row items-center gap-2 text-blue">
          {read_at === null && <ICircle size={10} />}
          {dateToLocalYMDHM(created_at)}
        </span>
        <strong>{t(`pushNotifications.${type}.title`, payload)}</strong>
        <span className="line-clamp-1 text-grey-label">{t(`pushNotifications.${type}.message`, payload)}</span>
      </div>
      <div className="flex place-content-center place-items-center">
        <IRight className="h-full fill-grey-label" size={26} />
      </div>
    </div>
  );
};

export const NotificationBox = ({ id, created_at, archived_at, type, payload, onArchive }: Notification & { onArchive: any }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onClick = () => {
    navigate(`/shipment/${payload.shipmentId}`);
  };

  return (
    <div className="flex flex-col gap-2" data-id={id}>
      <div className="flex flex-col gap-2">
        <span className="text-blue">{dateToLocalYMDHM(created_at)}</span>
        <strong>{t(`pushNotifications.${type}.title`, payload)}</strong>
        <span>{t(`pushNotifications.${type}.message`, payload)}</span>
      </div>

      <div className="mt-4 flex flex-row gap-2">
        {(payload.shipmentId as any) && (
          <Button className="blue w-min" onClick={onClick}>
            {t('components.alerts.buttons.seeMore')}
          </Button>
        )}
        {!archived_at && (
          <Button className="red w-min" onClick={onArchive(id)}>
            {t('components.alerts.buttons.archive')}
          </Button>
        )}
      </div>
    </div>
  );
};
